// SCR-OFF-04 (frozen mockups-off-v1, ADR-084): the installed APK is older than the minimum the business set. A full
// screen asks for the update; unsent items stay on the phone. The web app is always the latest, so it never shows this.
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, EmptyState, List, ListRow } from '../../design/kit';
import { loadSettings } from '../../data/settings';
import { Code } from '../../i18n/Isolate';

/** -1, 0 or 1 comparing "1.2.10" with "1.2.9" by number, not by text. */
export function compareVersions(a: string, b: string): number {
  const x = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const y = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** The minimum version the business requires, when this installed app is older (APK only). */
export function useRequiredUpdate(): string | null {
  const native = Capacitor.isNativePlatform();
  const settings = useQuery({
    queryKey: ['settings', 'minimum'],
    queryFn: loadSettings,
    enabled: native,
    staleTime: 5 * 60_000,
  });
  const min = settings.data?.min_app_version;
  return native && min && compareVersions(__APP_VERSION__, min) < 0 ? min : null;
}

export function UpdateRequired({ required }: { required: string }) {
  const { t } = useTranslation();
  const url = import.meta.env.VITE_APK_URL as string | undefined;
  return (
    <div className="yv-skeleton-stack" data-testid="update-required">
      <EmptyState
        icon="alert"
        title={t('up.title')}
        text={t('up.text')}
        action={
          url ? (
            <Button variant="main" icon="download" onClick={() => window.open(url, '_blank')}>
              {t('up.button')}
            </Button>
          ) : undefined
        }
      />
      <List label={t('up.title')}>
        <ListRow lead={{ icon: 'info' }} title={t('up.installed')} value={<Code>{__APP_VERSION__}</Code>} />
        <ListRow lead={{ icon: 'check' }} title={t('up.required')} value={<Code>{required}</Code>} />
      </List>
    </div>
  );
}
